import React from 'react';
import { Box, Link, Typography } from '@mui/material';
import TouchTooltip from '../../common/TouchTooltip';
import { TIMING_GROUPS, TIMING_TOOLTIP } from './constants';
import { fmtInt, stillWantedLine } from '../../../utils/buyerInsightsFormat';

// Same order and labels as the Products Discount timing chips; tints from light to dark.
const SEGMENT_COLORS = { partners_first: '#ef6c00', you_first: '#1976d2', same_week: '#78909c', only_you: '#6a1b9a' };

/**
 * Expanded brand (spec §1.4, part A): the Timing block — a proportional bar of the four Discount
 * timing groups, each segment and its count a link to that group in Products — and the
 * still-wanted line.
 */
const BrandTiming = ({ row, onOpen }) => {
  const counts = Object.fromEntries(row.timing.map((t) => [t.key, t.count]));
  const total = row.discounted_styles;
  return (
    <Box sx={{ py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <TouchTooltip title={TIMING_TOOLTIP}>
          <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
            Discount timing
            {total > 0 && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {`· of your ${fmtInt(total)} discounted ${total === 1 ? 'style' : 'styles'}`}
              </Typography>
            )}
          </Typography>
        </TouchTooltip>
        {total === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No discounted styles in this period
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', height: 10, borderRadius: 1, overflow: 'hidden', my: 1, maxWidth: 480 }}>
              {TIMING_GROUPS.filter((g) => counts[g.key] > 0).map((g) => (
                <TouchTooltip key={g.key} title={`${g.label}: ${fmtInt(counts[g.key])}`}>
                  <Box
                    role="link"
                    aria-label={`${g.label} ${counts[g.key]}`}
                    onClick={() => onOpen({ view: 'discount_timing', group: g.key }, 'timing_segment')}
                    sx={{ width: `${(100 * counts[g.key]) / total}%`, bgcolor: SEGMENT_COLORS[g.key], cursor: 'pointer' }}
                  />
                </TouchTooltip>
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5 }}>
              {TIMING_GROUPS.map((g) => (
                <Link
                  key={g.key}
                  component="button"
                  variant="body2"
                  underline="hover"
                  color="text.primary"
                  onClick={() => onOpen({ view: 'discount_timing', group: g.key }, 'timing_count')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}
                >
                  <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEGMENT_COLORS[g.key] }} />
                  {g.label} <b>{fmtInt(counts[g.key])}</b>
                </Link>
              ))}
              <Link component="button" variant="body2" onClick={() => onOpen({ view: 'discount_timing' }, 'timing_see_all')}>
                See in Products →
              </Link>
            </Box>
          </>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary">
        {stillWantedLine(row.still_wanted_sizes, 14, row.still_wanted_products)}
      </Typography>
    </Box>
  );
};

export default BrandTiming;
